import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mic, Square, Loader2, Lock } from "lucide-react";
import { useIsPremium } from "./ListenButton";
import { transcribe, stopAudio, voiceErrorMessage } from "../lib/voice";

const MAX_MS = 120000;

export function MicButton({ onTranscript, disabled }) {
  const premium = useIsPremium();
  const navigate = useNavigate();
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const timer = useRef(null);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch {
      toast.error("Microphone access was blocked");
      return;
    }
    stopAudio();
    const chunks = [];
    const r = new MediaRecorder(stream);
    r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    r.onstop = async () => {
      clearTimeout(timer.current);
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: r.mimeType || "audio/webm" });
      if (!blob.size) { setState("idle"); return; }
      setState("transcribing");
      try {
        const text = await transcribe(blob);
        if (text) onTranscript(text); else toast("Didn't catch that. Try again?");
      } catch (e) { toast.error(await voiceErrorMessage(e)); } finally { setState("idle"); }
    };
    rec.current = r;
    r.start();
    setState("recording");
    timer.current = setTimeout(() => r.state === "recording" && r.stop(), MAX_MS);
  };

  const click = () => {
    if (!premium) {
      toast("Voice conversation is a Premium feature", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    if (state === "recording") rec.current?.stop();
    else if (state === "idle") start();
  };

  const Icon = !premium ? Lock : state === "recording" ? Square : state === "transcribing" ? Loader2 : Mic;
  return (
    <button type="button" data-testid="chat-mic" onClick={click} disabled={disabled || state === "transcribing"}
      title={!premium ? "Premium: talk to your companion" : state === "recording" ? "Stop recording" : "Speak to your companion"}
      className={`px-4 rounded-full border flex items-center transition-colors disabled:opacity-50 ${state === "recording" ? "border-red-400 text-red-400 animate-pulse" : "border-border hover:border-primary/50 text-muted-foreground hover:text-primary"}`}>
      <Icon size={16} className={state === "transcribing" ? "animate-spin" : ""} />
    </button>
  );
}
